import React from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button, Badge } from './ui';
import { RecommendationBadge, RecommendationLevel } from './StatusBadge';
import { ScreeningAnalysis } from './ScreeningAnalysis';
import { AIScreeningBadge, AIScreeningDetail } from './AIScreeningAnalysis';

export interface Review {
  id: number;
  applicationId: number;
  summary: string;
  strengths: string | null;
  concerns: string | null;
  recommendation: RecommendationLevel;
  overallScore: number;
  srReviewId: string | null;
  rawResponse: Record<string, unknown> | null;
  // AI Model tracking (US-077)
  modelUsed: string | null;
  modelTier: 'fast' | 'standard' | 'premium' | null;
  inputTokens: number | null;
  outputTokens: number | null;
  processingTimeMs: number | null;
  costUsd: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface ReviewCardProps {
  review: Review;
  candidateName?: string;
  jobTitle?: string;
  onView?: (review: Review) => void;
  onEdit?: (review: Review) => void;
  className?: string;
  showFullDetails?: boolean;
  /** Screening answers from the application, passed through for analysis display */
  screeningAnswers?: Record<string, string> | null;
}

// Score is stored as 0-50 (score5 * 10). Convert to 0-100 for color thresholds.
const getScoreColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'text-green-600';
  if (score100 >= 60) return 'text-blue-600';
  if (score100 >= 40) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBgColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'bg-green-100';
  if (score100 >= 60) return 'bg-blue-100';
  if (score100 >= 40) return 'bg-amber-100';
  return 'bg-red-100';
};

export const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  candidateName,
  jobTitle,
  onView,
  onEdit,
  className = '',
  showFullDetails = false,
  screeningAnswers,
}) => {
  const createdDate = typeof review.createdAt === 'string'
    ? new Date(review.createdAt)
    : review.createdAt;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse strengths and concerns (stored as comma-separated or JSON)
  const parseList = (data: string | null | undefined): string[] => {
    if (!data) return [];
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [data];
    } catch {
      return data.split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  const strengths = parseList(review.strengths);
  const concerns = parseList(review.concerns);

  return (
    <Card className={className} variant="default">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {(candidateName || jobTitle) && (
              <div className="mb-2">
                {candidateName && (
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {candidateName}
                  </h3>
                )}
                {jobTitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{jobTitle}</p>
                )}
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <RecommendationBadge level={review.recommendation} size="md" />
              <AIScreeningBadge rawResponse={review.rawResponse} />
              {review.srReviewId && (
                <Badge variant="neutral" size="sm">
                  Synced
                </Badge>
              )}
            </div>
          </div>

          {/* Score Circle */}
          <div
            className={`
              flex-shrink-0 h-16 w-16 rounded-full flex flex-col items-center justify-center
              ${getScoreBgColor(review.overallScore)}
            `}
          >
            <span className={`text-2xl font-bold ${getScoreColor(review.overallScore)}`}>
              {(review.overallScore / 10).toFixed(1)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">/ 5</span>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {/* Summary */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary</h4>
          <p className={`text-sm text-gray-600 dark:text-gray-400 ${!showFullDetails ? 'line-clamp-3' : ''}`}>
            {review.summary}
          </p>
        </div>

        {showFullDetails && (
          <>
            {/* Strengths */}
            {strengths.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Strengths
                </h4>
                <ul className="space-y-1">
                  {strengths.map((strength, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-600 dark:text-gray-400 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-green-400"
                    >
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* AI Screening Analysis from pipeline (US-110) */}
            <AIScreeningDetail
              rawResponse={review.rawResponse}
              className="mb-4"
            />

            {/* Heuristic Screening Analysis - between strengths and concerns */}
            <ScreeningAnalysis
              screeningAnswers={screeningAnswers ?? null}
              reviewConcerns={concerns}
              className="mb-4"
            />

            {/* Concerns */}
            {concerns.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Concerns
                </h4>
                <ul className="space-y-1">
                  {concerns.map((concern, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-600 dark:text-gray-400 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-amber-400"
                    >
                      {concern}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          Reviewed: {formatDate(createdDate)}
        </div>

        {/* AI Model metadata (US-077) */}
        {review.modelUsed && (
          <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <span>{review.modelUsed.split('/').pop()}</span>
            {review.modelTier && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                review.modelTier === 'premium'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : review.modelTier === 'standard'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              }`}>
                {review.modelTier}
              </span>
            )}
            {review.costUsd && (
              <span>${parseFloat(review.costUsd).toFixed(4)}</span>
            )}
            {review.processingTimeMs && (
              <span>{(review.processingTimeMs / 1000).toFixed(1)}s</span>
            )}
          </div>
        )}
      </CardBody>

      {(onView || onEdit) && (
        <CardFooter>
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(review)}>
              Edit Review
            </Button>
          )}
          {onView && !showFullDetails && (
            <Button variant="secondary" size="sm" onClick={() => onView(review)}>
              View Full Review
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

ReviewCard.displayName = 'ReviewCard';

// Compact review summary for lists
export interface ReviewSummaryProps {
  review: Review;
  onClick?: (review: Review) => void;
  className?: string;
}

export const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  review,
  onClick,
  className = '',
}) => {
  return (
    <div
      className={`
        flex items-center gap-4 p-3 rounded-lg
        ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      onClick={() => onClick?.(review)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.(review)}
    >
      {/* Score */}
      <div
        className={`
          flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center
          ${getScoreBgColor(review.overallScore)}
        `}
      >
        <span className={`text-sm font-bold ${getScoreColor(review.overallScore)}`}>
          {(review.overallScore / 10).toFixed(1)}
        </span>
      </div>

      {/* Summary */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 line-clamp-1">{review.summary}</p>
        <RecommendationBadge level={review.recommendation} size="sm" />
      </div>

      {/* Synced indicator */}
      {review.srReviewId && (
        <Badge variant="success" size="sm">
          Synced
        </Badge>
      )}
    </div>
  );
};

ReviewSummary.displayName = 'ReviewSummary';
